import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useSyncExternalStore, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import "@fontsource-variable/inter/index.css";
import "@fontsource-variable/space-grotesk/index.css";
import "@fontsource-variable/jetbrains-mono/index.css";
import { Toaster } from "sonner";
import i18n, { applyStoredLanguage } from "@/i18n";
import { useTranslation } from "react-i18next";
import { registerPwa } from "@/pwa-register";
import { installClientIncidentHandlers, logIncident } from "@/lib/incident-log";
import { useProfile } from "@/lib/queries";
import {
  setFormattingCountry,
  subscribeFormattingCountry,
  getFormattingCountry,
} from "@/lib/format";

function NotFoundComponent() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("landing.getStarted")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    logIncident({
      severity: "critical",
      source: "client",
      event_type: "react_error_boundary",
      message: error?.message ? `${error.name}: ${error.message}` : String(error),
      metadata: { stack: error?.stack },
    });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#F4F3EF" },
      { property: "og:site_name", content: "VISIKT" },
      { property: "og:image", content: "https://visikt.com/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://visikt.com/og-image.png" },
      { name: "google-site-verification", content: "mFxrkcF-BIZOu9BY6MyFt9_8WFMvTNMFIaBcd5oopOE" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "icon", href: "/favicon.ico", sizes: "32x32" },
      { rel: "icon", type: "image/png", href: "/icon-512.png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "VISIKT",
          url: "https://visikt.com",
          logo: "https://visikt.com/icon-512.png",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "VISIKT",
          url: "https://visikt.com",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  // Subscribe the root to formatting-country changes. When the profile
  // country changes, the store notifies and React re-renders the whole
  // tree so already-mounted components re-run their formatters. This
  // replaces an earlier `queryClient.invalidateQueries()` that refetched
  // every cached query just to trigger a re-render.
  useSyncExternalStore(
    subscribeFormattingCountry,
    getFormattingCountry,
    () => null, // SSR snapshot — no profile country during prerender
  );
  useEffect(() => {
    const syncLang = () => {
      const lang = i18n.language === "nb" ? "nb" : "en";
      if (typeof document !== "undefined" && document.documentElement.lang !== lang) {
        document.documentElement.lang = lang;
      }
    };
    // Defer language switch until after hydration is complete to avoid mismatches.
    const id = setTimeout(() => {
      applyStoredLanguage();
      syncLang();
    }, 100);
    i18n.on("languageChanged", syncLang);
    registerPwa();
    installClientIncidentHandlers();
    return () => {
      clearTimeout(id);
      i18n.off("languageChanged", syncLang);
    };
  }, []);
  // Reference i18n so the import isn't tree-shaken before init runs.
  void i18n;

  return (
    <QueryClientProvider client={queryClient}>
      <FormattingCountrySync />
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <Toaster position="top-center" richColors closeButton />
    </QueryClientProvider>
  );
}

/**
 * Keeps the number-formatting locale in sync with the signed-in user's
 * profile country. Falls back to the UI language when no profile is
 * available (public routes, signed out, still loading). Notifying
 * subscribers of the change is handled inside `setFormattingCountry` —
 * `RootComponent` subscribes via `useSyncExternalStore` and re-renders
 * the tree, so no query invalidation / refetch is needed.
 */
function FormattingCountrySync() {
  const q = useProfile();
  const country = (q.data as { country?: string | null } | null | undefined)?.country ?? null;
  useEffect(() => {
    setFormattingCountry(country);
  }, [country]);
  return null;
}
